import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GrnLinesTable } from '@/components/grn/GrnLinesTable';
import { grnApi, grnErr, money, openGrnPdf } from '@/services/grnApi';

export const STATUS_TONE = { FINAL: 'border-emerald-300 bg-emerald-50 text-emerald-800', REOPENED: 'border-amber-300 bg-amber-50 text-amber-800',
  CANCELLED: 'border-rose-300 bg-rose-50 text-rose-700' };

const Earlier = ({ grns }) => (
  <div className="space-y-1" data-testid="grn-preview-existing">
    <p className="text-xs font-semibold text-gray-600">GRNs already made for this delivery</p>
    {grns.map((g) => (
      <div key={g.grn_id} className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-mono font-semibold">{g.grn_number}{g.revision ? ` (Rev ${g.revision})` : ''}</span>
        <Badge variant="outline" className={STATUS_TONE[g.status]}>{g.status}</Badge>
        <span className="tabular-nums">{money(g.totals?.grand_total)}</span>
        <button type="button" className="text-blue-700 hover:underline" onClick={() => openGrnPdf(g)} data-testid={`grn-preview-pdf-${g.grn_id}`}>PDF</button>
      </div>))}
  </div>
);

/** One delivery: what goes on its GRN now (PO rates + GST) → Finalise locks it for Accounts. */
export const GrnPreviewDialog = ({ entryId, onClose, onDone }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { setD(null); grnApi.delivery(entryId).then(setD).catch((e) => { toast.error(grnErr(e, 'Could not load the delivery')); onClose(); }); }, [entryId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (entryId) load(); }, [entryId, load]);
  if (!entryId) return null;
  const finalise = async () => {
    setBusy(true);
    try {
      const g = await grnApi.finalise(entryId);
      toast.success(`${g.grn_number}${g.revision ? ` (Rev ${g.revision})` : ''} finalised — ${money(g.totals.grand_total)}`);
      onDone(g);
    } catch (e) { toast.error(grnErr(e, 'Could not finalise the GRN'), { duration: 9000 }); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto [&>*]:min-w-0" data-testid="grn-preview-dialog">
        <DialogHeader>
          <DialogTitle>Goods Receipt Note {d?.reopened ? `— ${d.reopened} (reopened)` : ''}</DialogTitle>
          <DialogDescription>{d ? `${d.document_barcode || d.entry_id} · ${d.delivery_vendor || d.vendor?.vendor_name || ''}${d.vendor_doc_no ? ` · Invoice ${d.vendor_doc_no}` : ''}` : 'Loading…'}</DialogDescription>
        </DialogHeader>
        {!d ? <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin text-gray-400" /> : (
          <div className="space-y-4">
            {d.lines.length ? <>
              <p className="text-xs text-gray-600" data-testid="grn-preview-meta">{d.receipt_count} receipt(s) on {d.po_numbers.join(', ')} · {d.vendor?.vendor_state} supplier → {d.vendor?.gst_type === 'CGST_SGST' ? 'CGST + SGST' : 'IGST'}</p>
              <GrnLinesTable lines={d.lines} totals={d.totals} gstType={d.vendor?.gst_type} testId="grn-preview" />
            </> : <p className="rounded-lg border border-dashed p-6 text-center text-sm text-gray-500" data-testid="grn-preview-empty">Nothing counted on a PO is waiting for a GRN on this delivery.</p>}
            {d.grns.length > 0 && <Earlier grns={d.grns} />}
          </div>)}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="grn-preview-close">Close</Button>
          {d?.lines.length > 0 && <Button onClick={finalise} disabled={busy} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="grn-finalise-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}Finalise GRN</Button>}
        </DialogFooter>
        {d && <p className="flex items-center gap-1 text-[11px] text-gray-500"><CheckCircle2 className="h-3 w-3 shrink-0" />Once final, the amounts are locked and these receipts can't be reversed — only an Admin can reopen it.</p>}
      </DialogContent>
    </Dialog>
  );
};

export default GrnPreviewDialog;
